<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { userUserPicStore } from "@/stores/data/user-pic";
import {
  UserPICDataPayload,
  UserPICDataResponse,
} from "@/types/interface/user-pic";
import { mapOptionMultiselect } from "@/utils/helper";
import { useUserStore } from "@/stores/master/user";
import { useRoleStore } from "@/stores/master/role";
import { usePositionStore } from "@/stores/master/position";
import { useDepartmentStore } from "@/stores/master/department";
import { useVesselStore } from "@/stores/master/vessel";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

const idUserPic = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const userPicStore = userUserPicStore();
const detailUserPic = ref<UserPICDataResponse>();
const userStore = useUserStore();
const listUser = ref<any>();
const roleStore = useRoleStore();
const listRole = ref<any>();
const positionStore = usePositionStore();
const listPosition = ref<any>();
const departmentStore = useDepartmentStore();
const listDepartment = ref<any>();
const vesselStore = useVesselStore();
const listVessel = ref<any>();

const userSchema = zod
  .object({
    value: zod.number(),
    label: zod.string(),
  })
  .refine(
    (data) =>
      listUser.value.some(
        (code) => code.value === data.value && code.label === data.label
      ),
    {
      message: "Invalid user selected",
    }
  );

const roleSchema = zod
  .object({
    value: zod.number(),
    label: zod.string(),
  })
  .refine(
    (data) =>
      listRole.value.some(
        (code) => code.value === data.value && code.label === data.label
      ),
    {
      message: "Invalid role selected",
    }
  );

const jobSchema = zod
  .object({
    value: zod.number(),
    label: zod.string(),
  })
  .refine(
    (data) =>
      listPosition.value.some(
        (code) => code.value === data.value && code.label === data.label
      ),
    {
      message: "Invalid job selected",
    }
  );

const departmentSchema = zod
  .object({
    value: zod.number(),
    label: zod.string(),
  })
  .refine(
    (data) =>
      listDepartment.value.some(
        (code) => code.value === data.value && code.label === data.label
      ),
    {
      message: "Invalid department selected",
    }
  );

const vesselSchema = zod
  .object({
    value: zod.number(),
    label: zod.string(),
  })
  .refine(
    (data) =>
      listVessel.value.some(
        (code) => code.value === data.value && code.label === data.label
      ),
    {
      message: "Invalid vessel selected",
    }
  );

const validationSchema = toTypedSchema(
  zod.object({
    user_id: userSchema,
    role_id: roleSchema,
    job_id: jobSchema,
    department_id: departmentSchema,
    vessel_id: vesselSchema,
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: user_id } = useField<number>("user_id");
const { value: role_id } = useField<number>("role_id");
const { value: job_id } = useField<number>("job_id");
const { value: department_id } = useField<number>("department_id");
const { value: vessel_id } = useField<number>("vessel_id");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;

  const payloadUserPic: UserPICDataPayload = {
    user_id: user_id.value?.value,
    role_id: role_id.value?.value,
    job_id: job_id.value?.value,
    department_id: department_id.value?.value,
    vessel_id: vessel_id.value?.value,
  };

  const responseApi = ref<any>();

  if (isEdit.value == true) {
    responseApi.value = await userPicStore.dispatchUpdate(
      idUserPic.value,
      payloadUserPic
    );
  } else {
    responseApi.value = await userPicStore.dispatchCreate(payloadUserPic);
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "User PIC Successfully Updated"
          : "User PIC Successfully Added"
      );
      router.push("/data/user-pic");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadDetailUserPic = async () => {
  const { success, message } = await userPicStore.dispatchDetail(
    idUserPic.value
  );
  if (success) {
    detailUserPic.value = await userPicStore?.userPic?.data;
    user_id.value = mapOptionMultiselect(
      detailUserPic.value?.user,
      "id",
      "name"
    );
    job_id.value = mapOptionMultiselect(detailUserPic.value?.job, "id", "name");
    department_id.value = mapOptionMultiselect(
      detailUserPic.value?.department,
      "id",
      "name"
    );
    role_id.value = mapOptionMultiselect(
      detailUserPic.value?.role,
      "id",
      "name"
    );
    vessel_id.value = mapOptionMultiselect(
      detailUserPic.value?.vessel,
      "id",
      "name"
    );
  }
};

const loadListUser = async () => {
  const { success, message } = await userStore.dispatchGetUsers(1, 99);
  if (success) {
    listUser.value = mapOptionMultiselect(
      await userStore.users.items,
      "id",
      "name"
    );
  }
};

const loadListRole = async () => {
  const { success, message } = await roleStore.dispatchGetRoles(1, 99);
  if (success) {
    listRole.value = mapOptionMultiselect(
      await roleStore.roles.items,
      "id",
      "name"
    );
  }
};

const loadListPosition = async () => {
  const { success, message } = await positionStore.dispatchGetPositions(1, 99);
  if (success) {
    listPosition.value = mapOptionMultiselect(
      await positionStore.positions.items,
      "id",
      "name"
    );
  }
};

const loadListDepartment = async () => {
  const { success, message } = await departmentStore.dispatchGetDepartments(
    1,
    99
  );
  if (success) {
    listDepartment.value = mapOptionMultiselect(
      await departmentStore.departments.items,
      "id",
      "name"
    );
  }
};

const loadListVessel = async () => {
  const { success, message } = await vesselStore.dispatchGetAll(1, 99);
  if (success) {
    listVessel.value = mapOptionMultiselect(
      await vesselStore.vessels.items,
      "id",
      "name"
    );
  }
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idUserPic.value = router.currentRoute.value.params.id.toString();
    loadDetailUserPic();
  }
  loadListUser();
  loadListRole();
  loadListPosition();
  loadListDepartment();
  loadListVessel();
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create User PIC</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/data/user-pic"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List User PIC
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="user_id" class="form-label label text-secondary"
                    >User <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="user_id"
                      :options="listUser"
                      placeholder="Choose User"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.user_id }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="role_id" class="form-label label text-secondary"
                    >Role <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="role_id"
                      :options="listRole"
                      placeholder="Choose Role"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.role_id }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="job_id" class="form-label label text-secondary"
                    >Position <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="job_id"
                      :options="listPosition"
                      placeholder="Choose Position"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.job_id }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="department_id"
                    class="form-label label text-secondary"
                    >Department <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="department_id"
                      :options="listDepartment"
                      placeholder="Choose Department"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.department_id }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="vessel_id" class="form-label label text-secondary"
                    >Vessel <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="vessel_id"
                      :options="listVessel"
                      placeholder="Choose Vessel"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.vessel_id }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
